// NodeActivityField.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatDateTime } from 'utilities/DateTime';
import { getResolvedFields } from 'utilities/NodeType';

// Services
import { subscribeToNodeActivityEvents } from 'services/nodes/NodeActivityService';

// CSS
import './NodeActivityField.css';

const CORE_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  tags: 'Tags',
  coverUrl: 'Cover',
  permissions: 'Permissions',
};

const EVENT_LIMIT = 25;

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'number') return new Date(value);
  const parsed = new Date(value);
  return Number.isNaN(parsed?.getTime()) ? null : parsed;
};

const toSentenceCase = (value) => {
  if (!value) return '';
  const normalized = value.replace(/[_-]/g, ' ').trim();
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * NodeActivityField
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeActivityField = ({ node }) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!node?.key) {
      setEvents([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToNodeActivityEvents(
      { nodeKey: node.key, limit: EVENT_LIMIT },
      (nextEvents) => {
        setEvents(Array.isArray(nextEvents) ? nextEvents : []);
        setLoading(false);
      },
      (err) => {
        console.warn('⚠️ Unable to load node activity events:', err);
        setEvents([]);
        setError('We could not load recent activity.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [node?.key]);

  // ----- MEMOS ----- //

  const fieldLabels = useMemo(() => {
    if (!node?.type) {
      return { ...CORE_FIELD_LABELS };
    }

    const lookup = { ...CORE_FIELD_LABELS };
    const fields = getResolvedFields(node.type, { includeHidden: true, excludeSecured: true });

    fields.forEach((field) => {
      if (!field) return;
      const baseLabel = field.label || '';
      const sourceKey = String(field.source || node.type).toLowerCase();
      const dataKey = `data_${sourceKey}`;
      const fieldName = field.name ? field.name.trim() : '';

      if (typeof field.type === 'string' && field.type.endsWith('_form')) {
        if (baseLabel) {
          lookup[dataKey] = baseLabel;
        }
        return;
      }

      if (fieldName && baseLabel) {
        lookup[`${dataKey}.${fieldName}`] = baseLabel;
      }
    });

    return lookup;
  }, [node?.type]);

  if (!node) return null;

  // ----- UTILITIES ----- //

  const getActorName = (event) => {
    if (event.actorName) return event.actorName;
    const summary = event?.user;
    if (!summary) return 'Someone';
    if (summary.title) return summary.title;
    if (summary.user?.name) return summary.user.name;
    if (summary.userKey) return summary.userKey;
    return 'Someone';
  };

  const getFieldLabel = (path) => {
    const normalizedPath = String(path || '').trim();
    if (!normalizedPath) return 'Field';
    if (fieldLabels[normalizedPath]) return fieldLabels[normalizedPath];

    const segments = normalizedPath.split('.');
    for (let end = segments.length - 1; end > 0; end -= 1) {
      const candidatePath = segments.slice(0, end).join('.');
      if (fieldLabels[candidatePath]) {
        return fieldLabels[candidatePath];
      }
    }

    const fallback = segments.pop() || path;
    return toSentenceCase(fallback);
  };

  const renderEntry = (event) => {
    const occurredAt = toDate(event.occurredAt) || toDate(event.timestamp);
    const formattedTimestamp = occurredAt ? formatDateTime(occurredAt) : 'Just now';
    const fieldLabel = getFieldLabel(event.field);
    const normalizedLabel = (() => {
      if (!fieldLabel) return 'field';
      const trimmed = fieldLabel.trim();
      if (!trimmed) return 'field';
      return trimmed.toLowerCase();
    })();
    const message = `Updated ${normalizedLabel.startsWith('the ')
      ? normalizedLabel
      : `the ${normalizedLabel}`}`;

    const displayMessages = (() => {
      const fieldChangeMessages = Array.isArray(event?.changes)
        ? event.changes
          .map((change) => {
            const fieldPath = String(change?.fieldPath || change?.field || event?.field || '').trim();
            if (!fieldPath) return '';

            const resolvedFieldLabel = getFieldLabel(fieldPath);
            return resolvedFieldLabel ? `Updated ${resolvedFieldLabel}.` : '';
          })
          .filter(Boolean)
        : [];

      if (fieldChangeMessages.length > 0) {
        return [...new Set(fieldChangeMessages)];
      }

      const promptHints = Array.isArray(event?.promptHints)
        ? event.promptHints.map((entry) => String(entry || '').trim()).filter(Boolean)
        : [];

      if (promptHints.length > 0) {
        return promptHints;
      }

      const changeSummaries = Array.isArray(event?.changeSummaries)
        ? event.changeSummaries.map((entry) => String(entry || '').trim()).filter(Boolean)
        : [];

      if (changeSummaries.length > 0) {
        return changeSummaries;
      }

      return [message];
    })();

    const overflowCount = Math.max(0, displayMessages.length - 1);

    return (
      <li
        className="node-activity-field-entry"
        style={{
          borderLeft: `1px solid ${theme.backgroundMuted}`,
        }}
        key={event.key}
      >

        {/* ENTRY HEADER */}
        <div className="node-activity-field-entry-header">

          {/* ACTOR */}
          <span
            className="node-activity-field-actor"
          >
            {getActorName(event)} • {formattedTimestamp}
          </span>
        </div>

        {/* MESSAGE */}
        <div
          className="node-activity-field-message"
        >
          {displayMessages[0]}
        </div>

        {/* MESSAGE */}
        {overflowCount > 0 ? (
          <div
            className="node-activity-field-message"
          >
            +{overflowCount} more change{overflowCount === 1 ? '' : 's'}
          </div>
        ) : null}
      </li>
    );
  };

  // ----- RENDER ----- //

  return (
    <div className="node-activity-field-container">

      {/* EMPTY */}
      {error && (

        <div
          className="node-activity-field-empty"
        >
          {error}
        </div>
      )}

      {/* EMPTY */}
      {!error && loading && (

        <div
          className="node-activity-field-empty"
        >
          Loading history…
        </div>
      )}

      {/* EMPTY */}
      {!error && !loading && events.length === 0 && (

        <div
          className="node-activity-field-empty"
        >
          No changes have been logged for this item yet.
        </div>
      )}

      {/* LIST */}
      {!error && events.length > 0 && (

        <ul className="node-activity-field-list">
          {events.map((event) => renderEntry(event))}
        </ul>
      )}
    </div>
  );
};

// Dev Tools
NodeActivityField.displayName = 'NodeActivityField';

export default NodeActivityField;
